import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Check,
  Cube,
  Palette,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import {
  ForumPageChrome,
  ForumPageShell,
} from "@/components/forum-page-chrome";
import { cn } from "@/lib/cn";
import { humanizeHint } from "@/lib/humanize-hint";

export const Route = createFileRoute("/configure")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { yard?: string; platform?: string } => ({
    yard: typeof search.yard === "string" ? search.yard : undefined,
    platform:
      typeof search.platform === "string" ? search.platform : undefined,
  }),
  component: ConfigurePage,
});

const OPTIONS = [
  {
    groupKey: "configureOptEngine" as const,
    choices: ["Mercury 250 V8", "Mercury 300 V8", "Mercury 350 Verado"],
  },
  {
    groupKey: "configureOptSeating" as const,
    choices: ["Comfort", "Sport", "Premium Leather"],
  },
  {
    groupKey: "configureOptElectronics" as const,
    choices: ["Garmin Basic", "Garmin Premium", "Simrad Pro"],
  },
];

const FINISH = [
  { labelKey: "configureFinishHull" as const, value: "Arctic White" },
  { labelKey: "configureFinishStripe" as const, value: "Navy Blue" },
  { labelKey: "configureFinishUpholstery" as const, value: "Oyster" },
];

const SPECS = [
  { labelKey: "configureSpecLoa" as const, value: "12.8 m" },
  { labelKey: "configureSpecBeam" as const, value: "3.96 m" },
  { labelKey: "configureSpecDraft" as const, value: "0.95 m" },
  { labelKey: "configureSpecDisp" as const, value: "8 200 kg" },
  { labelKey: "configureSpecSpeed" as const, value: "42 kn" },
  { labelKey: "configureSpecRcd" as const, value: "Cat. B" },
];

/** Landing BoatRequest layout — mock until C4 boat intake. */
function ConfigurePage() {
  const t = useTranslations("app");
  const { yard, platform } = Route.useSearch();
  const [selected, setSelected] = useState<Record<number, number>>({
    0: 2,
    1: 0,
    2: 1,
  });

  const modelLabel =
    platform?.trim() || yard?.trim()
      ? [humanizeHint(platform), humanizeHint(yard)].filter(Boolean).join(" · ")
      : t("configureDemoModel");

  return (
    <ForumPageShell>
      <ForumPageChrome
        eyebrow={t("configureEyebrow")}
        title={
          <span className="inline-flex flex-wrap items-baseline gap-2">
            {t("configureTitle")}
            <span className="text-ui-nano font-semibold uppercase tracking-wide text-[var(--brand-text-muted)] opacity-60">
              {t("navLater")}
            </span>
          </span>
        }
        intro={t("configureIntro")}
        trailing={
          yard && platform ? (
            <Link
              to="/catalog"
              search={{ yard, platform }}
              className="text-[var(--brand-navy)] underline-offset-2 hover:underline"
            >
              {t("configureBackEtka")}
            </Link>
          ) : (
            <Link
              to="/catalog"
              className="text-[var(--brand-navy)] underline-offset-2 hover:underline"
            >
              {t("configureBackNetwork")}
            </Link>
          )
        }
      />

      <div className="overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
        <div className="flex items-center justify-between gap-3 border-b border-[var(--brand-border)] px-4 py-3 sm:px-6">
          <span className="font-heading text-ui-micro font-semibold text-[var(--brand-navy)]">
            {t("configurePanelTitle", { model: modelLabel })}
          </span>
          <span className="text-ui-micro text-[var(--brand-text-muted)]">
            {t("configureSubsetHint")}
          </span>
        </div>

        <div className="flex flex-col lg:flex-row">
          <div className="flex min-h-[280px] flex-1 items-center justify-center border-b border-[var(--brand-border)] bg-[color-mix(in_srgb,var(--brand-bg-section)_70%,transparent)] p-6 sm:p-10 lg:border-b-0 lg:border-r">
            <div className="text-center">
              <Cube
                className="mx-auto mb-3 size-14 text-[var(--brand-border)]"
                weight="regular"
              />
              <p className="font-heading text-ui-dense text-[var(--brand-navy)] opacity-60">
                {t("configurePreviewTitle")}
              </p>
              <p className="mt-1 text-ui-micro text-[var(--brand-text-muted)]">
                {t("configurePreviewHint")}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {FINISH.map((f) => (
                  <div
                    key={f.labelKey}
                    className="rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-1.5 text-ui-micro"
                  >
                    <span className="block text-[var(--brand-text-muted)]">
                      {t(f.labelKey)}
                    </span>
                    <span className="inline-flex items-center gap-1 font-medium text-[var(--brand-navy)]">
                      <Palette className="size-2.5" weight="bold" />
                      {f.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4 p-4 sm:p-5 lg:w-80 lg:shrink-0">
            {OPTIONS.map((og, gi) => (
              <div key={og.groupKey}>
                <p className="mb-2 font-heading text-ui-micro font-semibold text-[var(--brand-navy)]">
                  {t(og.groupKey)}
                </p>
                <div className="space-y-1.5">
                  {og.choices.map((c, ci) => {
                    const on = selected[gi] === ci;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() =>
                          setSelected((prev) => ({ ...prev, [gi]: ci }))
                        }
                        className={cn(
                          "w-full rounded-lg border px-3 py-2 text-left text-ui-micro transition-colors",
                          on
                            ? "border-[var(--brand-navy)] bg-[rgb(var(--brand-navy-rgb)/0.05)] font-medium text-[var(--brand-navy)]"
                            : "border-[var(--brand-border)] text-[var(--brand-text-muted)] hover:border-[color-mix(in_srgb,var(--brand-navy)_30%,var(--brand-border))]",
                        )}
                      >
                        <span className="flex items-center justify-between gap-2">
                          {c}
                          {on ? (
                            <Check
                              className="size-3.5 shrink-0 text-[var(--brand-navy)]"
                              weight="bold"
                            />
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="border-t border-[var(--brand-border)] pt-3">
              <p className="mb-2 font-heading text-ui-micro font-semibold text-[var(--brand-navy)]">
                {t("configureSpecsTitle")}
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {SPECS.map((s) => (
                  <div
                    key={s.labelKey}
                    className="rounded-lg bg-[var(--brand-bg-section)] p-2 text-center"
                  >
                    <span className="block text-ui-micro text-[var(--brand-text-muted)]">
                      {t(s.labelKey)}
                    </span>
                    <span className="block text-ui-micro font-medium text-[var(--brand-navy)]">
                      {s.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-[var(--brand-border)] pt-3">
              <div>
                <span className="block text-ui-micro text-[var(--brand-text-muted)]">
                  {t("configureFromPrice")}
                </span>
                <span className="font-heading text-lg font-bold text-[var(--brand-navy)]">
                  € 285 000
                </span>
              </div>
              <Button type="button" variant="primary" size="md" disabled>
                <PaperPlaneTilt className="size-3.5" weight="bold" />
                {t("configureSubmit")}
              </Button>
            </div>
            <p className="text-ui-micro text-[var(--brand-text-muted)] opacity-70">
              {t("configurePricesNote")}
            </p>
          </div>
        </div>
      </div>
    </ForumPageShell>
  );
}
